import { useId, useRef, useState, type KeyboardEvent } from "react";
import { aboutPhotos } from "../data/aboutPhotos";
import { Icon } from "./Icons";

type AboutPortraitProps = {
  variant?: "preview" | "page";
};

export default function AboutPortrait({ variant = "preview" }: AboutPortraitProps) {
  const [selected, setSelected] = useState(0);
  const choices = useRef<(HTMLButtonElement | null)[]>([]);
  const id = useId();
  const current = aboutPhotos[selected];
  const sizes = variant === "page"
    ? "(max-width: 540px) calc(100vw - 48px), (max-width: 800px) 460px, (max-width: 1800px) 36vw, 630px"
    : "(max-width: 540px) calc(100vw - 48px), (max-width: 800px) 43vw, (max-width: 1800px) 41vw, 712px";
  const onChoiceKey = (event: KeyboardEvent, index: number) => {
    const next = event.key === "ArrowRight" ? (index + 1) % aboutPhotos.length
      : event.key === "ArrowLeft" ? (index + aboutPhotos.length - 1) % aboutPhotos.length
        : event.key === "Home" ? 0 : event.key === "End" ? aboutPhotos.length - 1 : -1;
    if (next < 0) return;
    event.preventDefault();
    setSelected(next);
    choices.current[next]?.focus();
  };
  return (
    <div className={`about-portrait about-portrait-${variant}`} role="region" aria-label="Photos of Darren away from the screen">
      <div className="portrait-gallery-topline mono" aria-hidden="true">
        <span><span className="portrait-gallery-mark" />A little off-screen</span>
        <span>0{selected + 1}<span className="portrait-gallery-total"> / 0{aboutPhotos.length}</span></span>
      </div>
      <figure className="portrait-gallery-figure" id={`${id}-photo`}>
        <div className="about-portrait-frame">
          {aboutPhotos.map((photo, index) => <img
            key={photo.id}
            className="portrait-gallery-slide"
            data-active={selected === index}
            aria-hidden={selected !== index}
            src={photo.src}
            srcSet={photo.srcSet}
            sizes={sizes}
            alt={photo.alt}
            width={photo.width}
            height={photo.height}
            loading={variant === "page" && index === 0 ? "eager" : "lazy"}
            decoding="async"
          />)}
        </div>
        <figcaption aria-live="polite" aria-atomic="true">
          <span className="sr-only">Photo {selected + 1} of {aboutPhotos.length}. </span>
          <span className="mono">{current.label}</span>
          <span>{current.caption}</span>
        </figcaption>
      </figure>
      <div className="portrait-gallery-choices" role="group" aria-label="Choose a photo">
        {aboutPhotos.map((photo, index) => <button
          key={photo.id}
          ref={element => { choices.current[index] = element; }}
          type="button"
          aria-label={`Show photo ${index + 1}: ${photo.title}`}
          aria-pressed={selected === index}
          aria-controls={`${id}-photo`}
          onClick={() => setSelected(index)}
          onKeyDown={event => onChoiceKey(event, index)}
        >
          <img src={photo.thumbnail} width="160" height={Math.round(160 * photo.height / photo.width)} alt="" loading="lazy" decoding="async" />
          <span><span className="mono">0{index + 1}</span><strong>{photo.title}</strong></span>
          <Icon name="diagonal" />
        </button>)}
      </div>
      <noscript><p className="portrait-gallery-noscript">More of me away from the screen: <a href={aboutPhotos[1].src}>view the running photo</a>.</p></noscript>
    </div>
  );
}
